import { DateTime, Duration } from 'luxon';

export type DurationUnit = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | 'millisecond';

export interface IDuration {
    unit: DurationUnit;
    value: number;
}

const unitStringToIndex = {
    year: 0,
    month: 1,
    day: 2,
    hour: 3,
    minute: 4,
    second: 5,
    millisecond: 6,
};

const unitIndexToString: DurationUnit[] = ['year', 'month', 'day', 'hour', 'minute', 'second', 'millisecond'];

const dePluralize = (word: string) => {
    const pluralized = word.endsWith('s') ? word.slice(0, -1) : word;
    return pluralized;
};

export const roundToNearestTime = ({
    time,
    roundTo,
    timezone = 'UTC',
    operation = 'round',
}: {
    time: string;
    roundTo: IDuration;
    timezone?: string;
    operation?: 'floor' | 'ceil' | 'round';
}) => {
    const dateTime = DateTime.fromISO(time).setZone(timezone);
    const { unit, value } = roundTo;
    const roundToValue = Math.abs(value);
    const roundToUnit = dePluralize(unit) as DurationUnit;

    if (roundToValue === 0) return dateTime.toISO();
    const roundToValueInMillis = Duration.fromObject({ [roundToUnit]: roundToValue }).as('milliseconds');

    if (roundToUnit === 'year') {
        const valueInYears = Math[operation](dateTime.plus({ minutes: dateTime.offset }).toMillis() / roundToValueInMillis) * value;
        return DateTime.fromObject({ year: 1970 + valueInYears }, { zone: timezone }).toISO();
    }

    const valueAboveRoundToUnit = unitIndexToString[unitStringToIndex[roundToUnit] - 1];

    const valueToRound = dateTime
        .diff(dateTime.startOf(valueAboveRoundToUnit).minus({ [roundToUnit]: roundToUnit === 'day' || roundToUnit === 'month' ? 1 : 0 }))
        .as('milliseconds');
    const valueInMillis = Math[operation](valueToRound / roundToValueInMillis) * roundToValueInMillis;

    const newDateTime = dateTime
        .startOf(valueAboveRoundToUnit)
        .minus({ [roundToUnit]: roundToUnit === 'day' || roundToUnit === 'month' ? 1 : 0 })
        .plus({ milliseconds: valueInMillis });

    return newDateTime.toISO();
};
